import type {
  CreateDrugPrescriptionsRequest,
  RegisteredPrescriptionResponse,
} from '@/entities/prescription/api/prescription.api';
import { downloadImageZip } from '@/shared/utils/downloadImageZip';
import { downloadXlsx, exportXlsxTemplate } from '@/shared/utils/xlsxExport';

/** 레거시 `prescriptionReviewXLSXHeaderMap` */
export const PRESCRIPTION_DRUG_UPLOAD_HEADER_MAP: Record<string, string> = {
  prescriptionImageCode: '이미지 고유 코드',
  userEmail: '사용자 이메일',
  clinicName: '병의원명',
  drugCode: '의약품 코드',
  outHospitalQuantity: '원외 사용량',
  inHospitalQuantity: '원내 사용량',
};

export const REGISTERED_PRESCRIPTION_EXPORT_HEADERS: Record<string, string> = {
  prescriptionImageCode: '이미지 고유 코드',
  userEmail: '사용자 이메일',
  clinicName: '병의원명',
  drugCode: '의약품 코드',
  outHospitalQuantity: '원외 사용량',
  inHospitalQuantity: '원내 사용량',
  prescriptionYearMonth: '처방 년월',
  settlementYearMonth: '정산 년월',
};

export const downloadPrescriptionDrugUploadTemplate = () => {
  exportXlsxTemplate(
    PRESCRIPTION_DRUG_UPLOAD_HEADER_MAP,
    '처방정보_업로드_양식.xlsx',
  );
};

export const mapCsvRowToDrugUpload = (
  row: Record<string, string>,
): CreateDrugPrescriptionsRequest => ({
  prescriptionImageCode: row.prescriptionImageCode,
  drugCode: row.drugCode || undefined,
  outHospitalQuantity: Number(
    String(row.outHospitalQuantity ?? '0').replace(/,/g, ''),
  ),
  inHospitalQuantity: Number(
    String(row.inHospitalQuantity ?? '0').replace(/,/g, ''),
  ),
});

/** XLSX/CSV 파싱 행을 업로드 요청으로 변환 */
export const mapDrugUploadRow = (
  row: Record<string, unknown>,
): CreateDrugPrescriptionsRequest =>
  mapCsvRowToDrugUpload({
    prescriptionImageCode: String(row.prescriptionImageCode ?? ''),
    drugCode: row.drugCode != null ? String(row.drugCode) : '',
    outHospitalQuantity: String(row.outHospitalQuantity ?? '0'),
    inHospitalQuantity: String(row.inHospitalQuantity ?? '0'),
  });

export const collectUniqueImageUrls = (
  rows: Array<{ imageUrl?: string; s3FileKey?: string }>,
): string[] => {
  const urls: string[] = [];
  for (const item of rows) {
    const url = item.imageUrl || item.s3FileKey;
    if (url && !urls.includes(url)) {
      urls.push(url);
    }
  }
  return urls;
};

export const downloadRegisteredPrescriptionsCsv = (
  rows: RegisteredPrescriptionResponse[],
  settlementYearMonth: string,
) => {
  const mapped = rows.map((row) => {
    const out: Record<string, unknown> = {};
    for (const [key, label] of Object.entries(REGISTERED_PRESCRIPTION_EXPORT_HEADERS)) {
      out[label] = row[key as keyof RegisteredPrescriptionResponse];
    }
    return out;
  });
  downloadXlsx(mapped, `${settlementYearMonth}_처방내역.xlsx`);
};

export const downloadRegisteredPrescriptionImages = async (
  rows: RegisteredPrescriptionResponse[],
  settlementYearMonth: string,
) => {
  const urls = collectUniqueImageUrls(rows);
  await downloadImageZip(urls, `${settlementYearMonth}_처방내역_이미지`);
};
